import { create } from '@thoughtspot/logger';
const logger = create('chart-to-ts > native-chart-events');
const JSON_HTTP_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
};
const SSE_HTTP_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
};
const MUZE_STUDIO_CODEGEN_API_PATH = '/muze-studio/codegen/query';
const SESSIONS_API_BASE = '/muze-ai/api/v1/sessions';
const EXTRACT_TEMPLATE_API_PATH = '/muze-ai/api/v1/extract-template';
const GENERATE_CHART_TOOL = 'generate_chart';
const SPEC_PRODUCING_TOOLS = ['generate_spec', 'follow_up'];
function isGenerateChartTool(name) {
    return typeof name === 'string' && name.endsWith(GENERATE_CHART_TOOL);
}
function isSpecProducingTool(name) {
    return typeof name === 'string' && SPEC_PRODUCING_TOOLS.some((tool) => name.endsWith(tool));
}
function extractSpec(response) {
    if (!response || typeof response !== 'object')
        return undefined;
    const structured = response.structuredContent;
    if (structured && structured.spec != null)
        return structured.spec;
    if (response.spec != null)
        return response.spec;
    const content = response.content;
    if (Array.isArray(content)) {
        for (const part of content) {
            if (part?.type === 'text' && typeof part.text === 'string') {
                try {
                    const parsed = JSON.parse(part.text);
                    if (parsed.spec != null)
                        return parsed.spec;
                }
                catch {
                }
            }
        }
    }
    return undefined;
}
function isPresentCode(v) {
    if (typeof v === 'string')
        return v.length > 0;
    return typeof v === 'object' && v !== null;
}
function asString(v) {
    return typeof v === 'string' && v.length > 0 ? v : undefined;
}
function extractChartResult(response) {
    if (!response || typeof response !== 'object')
        return {};
    const structured = response.structuredContent;
    if (structured && isPresentCode(structured.code)) {
        return { code: structured.code, message: asString(structured.message) };
    }
    if (isPresentCode(response.code)) {
        return { code: response.code, message: asString(response.message) };
    }
    const content = response.content;
    if (Array.isArray(content)) {
        for (const part of content) {
            if (part?.type === 'text' && typeof part.text === 'string') {
                try {
                    const parsed = JSON.parse(part.text);
                    if (isPresentCode(parsed.code)) {
                        return { code: parsed.code, message: asString(parsed.message) };
                    }
                }
                catch {
                }
            }
        }
    }
    return {};
}
function parseSSELine(line) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(':') || !trimmed.startsWith('data:')) {
        return null;
    }
    const dataStr = trimmed.slice(5).trim();
    if (dataStr === '[DONE]')
        return null;
    try {
        return JSON.parse(dataStr);
    }
    catch {
        return null;
    }
}
function accumulateFromEvent(rawEvent, acc) {
    const parts = rawEvent?.content?.parts;
    if (!Array.isArray(parts))
        return;
    for (const part of parts) {
        if (isGenerateChartTool(part.function_response?.name)) {
            const { code, message } = extractChartResult(part.function_response?.response);
            if (code)
                acc.code = code;
            if (message)
                acc.message = message;
        }
        else if (isSpecProducingTool(part.function_response?.name)) {
            const spec = extractSpec(part.function_response?.response);
            if (spec != null)
                acc.spec = spec;
        }
        else if (typeof part.text === 'string' && part.text && !part.thought) {
            acc.message = part.text;
        }
    }
}
async function consumeQuerySSE(reader, onChunk) {
    const decoder = new TextDecoder();
    let buffer = '';
    const acc = {};
    let streamError = null;
    const handleLine = (line) => {
        const envelope = parseSSELine(line);
        if (!envelope)
            return;
        onChunk(envelope);
        const err = envelope.error ?? envelope.result?.error;
        if (err) {
            streamError = err.message || `agent error (${err.code ?? 'unknown'})`;
            return;
        }
        accumulateFromEvent(envelope.result?.raw_event, acc);
    };
    while (true) {
        const { done, value } = await reader.read();
        if (done)
            break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        lines.forEach(handleLine);
    }
    if (buffer.trim())
        handleLine(buffer);
    reader.releaseLock();
    if (streamError)
        throw new Error(streamError);
    return acc;
}
async function readErrorMessage(response) {
    try {
        const json = (await response.json());
        return asString(json?.message);
    }
    catch {
        return undefined;
    }
}
async function executeQuery(sessionId, body, onChunk) {
    const response = await fetch(`${window.location.origin}${SESSIONS_API_BASE}/${encodeURIComponent(sessionId)}/query`, {
        method: 'POST',
        headers: SSE_HTTP_HEADERS,
        credentials: 'include',
        body,
    });
    if (!response.ok) {
        const message = await readErrorMessage(response);
        throw new Error(message ?? `HTTP error! status: ${response.status}`);
    }
    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error('Response has no readable stream body');
    }
    const result = await consumeQuerySSE(reader, onChunk);
    result.sessionId = sessionId;
    return { isValid: true, data: result };
}
async function fetchJson(apiPath, method, body) {
    const response = await fetch(`${window.location.origin}${apiPath}`, {
        method,
        headers: JSON_HTTP_HEADERS,
        credentials: 'include',
        ...(body ? { body } : {}),
    });
    if (!response.ok) {
        const message = await readErrorMessage(response);
        throw new Error(message ?? `HTTP error! status: ${response.status}`);
    }
    return { isValid: true, data: await response.json() };
}
async function executeJson(path, method, body) {
    return fetchJson(`${SESSIONS_API_BASE}${path}`, method, body);
}
async function executeExtractTemplate(body) {
    return fetchJson(EXTRACT_TEMPLATE_API_PATH, 'POST', body);
}
export async function handleMuzeAiRequest(req, opts = {}) {
    const { apiType, sessionId, body, convKey } = req;
    try {
        if ((apiType === 'get-session' ||
            apiType === 'delete-session' ||
            apiType === 'stop' ||
            apiType === 'query') &&
            !sessionId) {
            return { isValid: false, errors: [`sessionId is required for ${apiType}`] };
        }
        switch (apiType) {
            case 'create-session':
                return await executeJson('', 'POST', body);
            case 'find-session': {
                if (!convKey) {
                    return { isValid: false, errors: ['convKey is required for find-session'] };
                }
                const filter = `labels[conv_key]=${encodeURIComponent(convKey)}`;
                return await executeJson(`?${filter}`, 'GET');
            }
            case 'get-session':
                return await executeJson(`/${encodeURIComponent(sessionId)}`, 'GET');
            case 'delete-session':
                return await executeJson(`/${encodeURIComponent(sessionId)}`, 'DELETE');
            case 'stop':
                return await executeJson(`/${encodeURIComponent(sessionId)}:stop`, 'POST', body);
            case 'query': {
                const onChunk = opts.onChunk ?? (() => undefined);
                return await executeQuery(sessionId, body, onChunk);
            }
            case 'extract-template':
                return await executeExtractTemplate(body);
            default: {
                const response = await fetch(`${window.location.origin}${MUZE_STUDIO_CODEGEN_API_PATH}`, {
                    method: 'POST',
                    headers: JSON_HTTP_HEADERS,
                    credentials: 'include',
                    body,
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return { isValid: true, data: await response.json() };
            }
        }
    }
    catch (error) {
        logger.warn('Error handling muze-ai request:', error);
        return {
            isValid: false,
            errors: [error instanceof Error ? error.message : 'Unknown error occurred'],
        };
    }
}
//# sourceMappingURL=index.js.map